import { type ComponentProps, type ReactNode } from 'react';
import { Navigate } from '@tanstack/react-router';
import { ChevronLeft, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs.tsx';
import { PageTitle } from '@/components/ui/Title.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import ScreenTabTileContent, {
  screenTabTileClass,
  screenTabTileGridClass,
} from '@/components/ui/ScreenTabTile.tsx';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/Tooltip.tsx';
import { useIsMobile } from '@/hooks/use-mobile.tsx';

export type ScreenTab<T extends string> = {
  value: T;
  label: string;
  icon: LucideIcon;
  /** Podpis pod nazwą na kaflu menu mobilnego — po co tu wchodzić. */
  hint: string;
};

type TabScreenProps<T extends string> = {
  tabs: readonly ScreenTab<T>[];
  /**
   * Aktywna zakładka. `undefined` to osobny stan, nie brak danych: na wąskim
   * ekranie znaczy „menu”, na szerokim — pierwszą zakładkę.
   */
  value: T | undefined;
  onChange: (value: T | undefined) => void;
  title: ReactNode;
  /** Zdanie nad siatką kafli. Widoczne tylko w menu mobilnym. */
  menuDescription?: ReactNode;
  /**
   * Główna akcja ekranu. Osobno od `actions`, bo to `TabScreen` rozstrzyga, czy
   * jest miejsce na jej etykietę — patrz `renderActions`.
   */
  primaryAction?: {
    label: string;
    icon: LucideIcon;
    onClick: () => void;
  };
  /** Pozostałe akcje przy tytule — w każdym z trzech stanów, bez zmian. */
  actions?: ReactNode;
  /** `TabsContent` poszczególnych zakładek. */
  children: ReactNode;
};

/**
 * Ekran zbudowany z zakładek, w dwóch wcieleniach.
 *
 * Szeroko: tytuł i pasek zakładek, jak dotąd. Poniżej `md` pasek się nie mieści
 * — zamiast go ścisnąć albo przewijać w bok, zakładki stają się ekranem-menu z
 * siatką kafli, a wybór wchodzi w zakładkę z paskiem powrotu. Dzięki temu, że
 * „menu” to brak wartości w adresie, stan jest linkowalny i przetrwa odświeżenie.
 *
 * Treść nieaktywnej zakładki nie jest montowana, więc menu mobilne nie ciągnie
 * danych żadnej z nich.
 */
const TabScreen = <T extends string>({
  tabs,
  value,
  onChange,
  title,
  menuDescription,
  primaryAction,
  actions,
  children,
}: TabScreenProps<T>) => {
  const isMobile = useIsMobile();
  const activeValue = value ?? (isMobile ? undefined : tabs[0]?.value);
  const activeTab = tabs.find((tab) => tab.value === activeValue);

  /*
   * Szeroko brak zakładki w adresie znaczy „pierwsza”, więc adres ma to
   * powiedzieć wprost: inaczej link skopiowany z paska wraca na telefonie do
   * menu zamiast do zakładki, którą się właśnie oglądało. Ta odnoga jest
   * reaktywna, bo poszerzenie okna nie uruchamia ponownie `beforeLoad` trasy.
   */
  const fillsTabInUrl = !isMobile && value === undefined && Boolean(tabs[0]);

  /*
   * `TabScreen` nie zna drzewa tras, a `search` jest w nim typowane sumą
   * parametrów wszystkich ekranów — stąd rzutowanie zamiast literału.
   */
  const fillFirstTabInUrl = {
    to: '.',
    replace: true,
    search: (prev: Record<string, unknown>) => ({
      ...prev,
      tab: tabs[0]?.value,
    }),
  } as unknown as ComponentProps<typeof Navigate>;

  /**
   * `collapsePrimary` zwija akcję główną do samej ikony. Włącza je tylko pasek
   * zakładki na telefonie: dzieli szerokość z powrotem i nazwą zakładki, a
   * pełna etykieta zjadała tam tyle miejsca, że z „Zaplanowane wydatki”
   * zostawało „Zaplan…”. W menu i na szerokim ekranie miejsce jest, więc
   * przycisk zostaje podpisany.
   */
  const renderActions = (collapsePrimary: boolean, className?: string) => {
    if (!primaryAction && !actions) return null;

    return (
      <div className={cn('flex items-center justify-end gap-2', className)}>
        {primaryAction &&
          (collapsePrimary ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <ActionButton
                  tone="accent"
                  size="sm"
                  iconOnly
                  aria-label={primaryAction.label}
                  onClick={primaryAction.onClick}
                >
                  <primaryAction.icon />
                </ActionButton>
              </TooltipTrigger>
              <TooltipContent>{primaryAction.label}</TooltipContent>
            </Tooltip>
          ) : (
            <ActionButton
              tone="accent"
              size="sm"
              onClick={primaryAction.onClick}
            >
              <primaryAction.icon />
              {primaryAction.label}
            </ActionButton>
          ))}
        {actions}
      </div>
    );
  };

  if (isMobile && !activeTab) {
    return (
      <div>
        <div className="flex flex-nowrap items-center justify-between gap-3">
          <PageTitle className="min-w-0 flex-1 truncate">{title}</PageTitle>
          {renderActions(false, 'shrink-0')}
        </div>
        {menuDescription && (
          <p className="mt-2 text-[13.5px] leading-normal text-neutral-500">
            {menuDescription}
          </p>
        )}
        <div className={cn('mt-4', screenTabTileGridClass)}>
          {tabs.map((tab) => (
            /*
             * Kafel jest celem nawigacji o trzech liniach treści, nie akcją —
             * `ActionButton` nie ma dla niego kształtu.
             */
            <button
              key={tab.value}
              type="button"
              onClick={() => onChange(tab.value)}
              className={screenTabTileClass}
            >
              <ScreenTabTileContent
                icon={tab.icon}
                label={tab.label}
                hint={tab.hint}
              />
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <Tabs
      value={activeValue}
      onValueChange={(next) => onChange(next as T)}
      className="flex w-full flex-col"
    >
      {fillsTabInUrl && <Navigate {...fillFirstTabInUrl} />}
      {isMobile ? (
        <div className="flex flex-nowrap items-center gap-2.5 border-b border-neutral-200 pb-3.5">
          {/* Skala `md` (44 px), nie „toolbar” — powrót jest tu głównym celem
              dotyku, a nie kontrolką w rzędzie narzędzi. */}
          <ActionButton
            tone="outline"
            size="md"
            iconOnly
            aria-label="Wróć do menu"
            onClick={() => onChange(undefined)}
          >
            <ChevronLeft />
          </ActionButton>
          {/* Rząd się nie łamie: to tytuł ustępuje miejsca (do wielokropka).
              Druga linia nagłówka jest gorsza niż skrócona nazwa zakładki, do
              której i tak przed chwilą się weszło — a że akcja główna jest tu
              samą ikoną, do skracania zwykle nie dochodzi. */}
          <span className="min-w-0 flex-1 truncate text-base font-extrabold tracking-[-0.01em] text-base-text">
            {activeTab?.label}
          </span>
          {renderActions(true, 'ml-auto shrink-0')}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <PageTitle>{title}</PageTitle>
            {renderActions(false)}
          </div>
          <TabsList className="mb-0 mt-3.5 w-full">
            {tabs.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>
                <tab.icon size={15} />
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </>
      )}
      <div className="mt-5">{children}</div>
    </Tabs>
  );
};
TabScreen.displayName = 'TabScreen';

export default TabScreen;
